<template>
    <div class="flex flex-col w-full items-center">
        <div class="text-sm text-gdc-gray-900 mb-1">{{ firstItem }} – {{ lastItem }} {{ outOfText }} {{ total }}</div>
        <ul class="automation-pagination flex flex-row !ps-0 !list-none justify-center">
            <li v-on="!isInFirstPage ? { click: onClickPreviousPage } : {}"
                class="!m-0 !mx-1 !p-0 !w-4 !h-8 leading-8 inline-flex justify-center items-center">
                <svg :class="[
                        'w-4 rotate-90', { 'text-gdc-blue-600 cursor-pointer' : !isInFirstPage, 'text-gdc-gray-600': isInFirstPage}]"
                     fill="none"  stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 10" aria-hidden="true">
                    <path d="M15 1.2l-7 7-7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </li>
            <li v-for="page in pages"
                :key="page.label + Math.random()"
                :class="['!m-0 !mx-1 !p-0 !w-8 !h-8 leading-8 text-gdc-blue-600 border border-gdc-blue-600',
                            'rounded cursor-pointer text-center hover:bg-gdc-blue-600 hover:text-white', getClassList(page), ]"
                @click="onClickPage(page.label)">
                {{ page.label }}
            </li>
            <li v-on="!isInLastPage ? { click: onClickNextPage } : {}"
                class="!m-0 !mx-1 !p-0 !w-4 !h-8 leading-8 inline-flex justify-center items-center">
                <svg :class="[
                        'w-4 -rotate-90', { 'text-gdc-blue-600 cursor-pointer' : !isInLastPage, 'text-gdc-gray-600': isInLastPage}]"
                     fill="none"  stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 10" aria-hidden="true">
                    <path d="M15 1.2l-7 7-7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </li>
        </ul>
    </div>
</template>

<script type="text/javascript">
export default
{
    name: 'pagination-tw',

    /**
     * Component Props
     */
    props: {
        maxVisibleButtons: {
            type: Number,
            required: false,
            default: 5,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        total: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
        },
        currentPage: {
            type: Number,
            required: true,
        },
        showFirstLastButtons: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        /**
         * get First Item
         * @return {number}
         */
        firstItem()
        {
            return (this.currentPage - 1) * this.perPage + 1;
        },

        /**
         * @return {Number|number}
         */
        lastItem()
        {
            return this.isInLastPage ? this.total : (this.firstItem + this.perPage - 1);
        },

        /**
         * @return {number}
         */
        startPage()
        {
            if (this.currentPage === 1)
            {
                return 1;
            }

            if (this.currentPage === this.totalPages && this.totalPages >= this.maxVisibleButtons)
            {
                return this.totalPages - this.maxVisibleButtons + 1;
            }

            return this.currentPage - 1;
        },

        /**
         * @return {number}
         */
        endPage()
        {
            return Math.min(this.startPage + this.maxVisibleButtons - 1, this.totalPages);
        },

        /**
         * @return {Array}
         */
        pages()
        {
            return this.fetchLinksWithClasses(this.currentPage, this.totalPages);
        },

        /**
         * @return {boolean}
         */
        isInFirstPage()
        {
            return this.currentPage === 1;
        },

        /**
         * @return {boolean}
         */
        isInLastPage()
        {
            return this.currentPage === this.totalPages;
        },

        /**
         *
         * @return {string}
         */
        firstButtonText()
        {
            return window.translations.pagination.first || 'First';
        },

        /**
         * @return {string}
         */
        lastButtonText()
        {
            return window.translations.pagination.last || 'Last';
        },

        /**
         * @return {*|string}
         */
        outOfText()
        {
            return window.translations.pagination.out_of || 'out of';
        },
    },
    methods: {

        /**
         * Get Class List for the page
         *
         * @param page
         */
        getClassList(page)
        {
            return {
                'bg-gdc-blue-600 text-white': this.isPageActive(page.label),
                'border-gdc-gray-600 text-gdc-gray-600 hover:!bg-white hover:!text-gdc-gray-600 hover:!cursor-defaults':
                    (typeof page.label === 'string' || page.label instanceof String),
                'hidden lg:block': page.class === 'hideMobile',
                'block lg:hidden': page.class === 'showMobileOnly',
            };
        },

        /**
         * @return void
         */
        onClickFirstPage()
        {
            this.$emit('pagechanged', 1);
        },

        /**
         * @return void
         */
        onClickPreviousPage()
        {
            this.$emit('pagechanged', this.currentPage - 1);
        },

        /**
         * @return void
         */
        onClickPage(page)
        {
            if (typeof page === 'string' || page instanceof String)
            {
                return;
            }

            this.$emit('pagechanged', page);
        },

        /**
         * @return void
         */
        onClickNextPage()
        {
            this.$emit('pagechanged', this.currentPage + 1);
        },

        /**
         * @return void
         */
        onClickLastPage()
        {
            this.$emit('pagechanged', this.totalPages);
        },

        /**
         * @return boolean
         */
        isPageActive(page)
        {
            return this.currentPage === page;
        },

        /**
         * TODO: might need refactoring at some point?
         *
         * This function is coming from App\Support\PaginationHelpers that also has a test file
         * Fetch the pagination button links
         * This will return the array like [1, 2, 3, 4, 5] or [1, 2, 3, '...', 7]
         * We always want maximum of 5 links
         *
         * @return array
         * @param currentPage
         * @param totalPages
         */
        fetchLinks(currentPage = 1, totalPages = 1)
        {
            let links = [];
            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i += 1) {
                    links.push(i);
                }
                return links;
            }

            // setup array [1,2,3,4,5];
            for (let i = 1; i <= 5; i += 1) {
                links.push(i);
            }

            let firstPage = 1;
            for (let pageIndex = 1; pageIndex <= links.length; pageIndex++) {
                switch (pageIndex) {
                    case 1:
                        break;
                    case 2:
                        // if current is `close (less than 2 away) ` to the first page - we want to add the 1,2,3
                        if (currentPage - 2 === firstPage) {
                            links[1] = currentPage - 1;
                        } // current page is lower or equal to our default - so just add 2
                        else if (currentPage === 1 || currentPage === 2) {
                            links[1] = 2;
                        } // current page is to `far` so we will add the dots
                        else {
                            links[1] = '...';
                        }
                        break;
                    case 3:
                        // if we are at the end - we want to add the last 3 numbers - 18,19,20 as last 3 buttons
                        if (currentPage === totalPages) {
                            links[2] = currentPage - 2;
                        } // if current is just 1 number before the last page - update this one to that number
                        else if (currentPage === (totalPages - 1)) {
                            links[2] = currentPage - 1;
                        } // if current page is bigger than 2 or less than 2 from last number - update this middle page
                        else if (currentPage > (firstPage + 1) && currentPage < (totalPages - 1)) {
                            if (currentPage - 2 > firstPage) {
                                links[2] = currentPage;
                            } else if (currentPage + 2 < totalPages) {
                                links[2] = currentPage;
                            }
                        }
                        break;
                    case 4:
                        // if we are at the end - we want to add the last 3 numbers - 18,19,20 as last 3 buttons
                        if (currentPage === totalPages) {
                            links[3] = currentPage - 1;
                        } // if current is just 1 number before the last page - update this one to that number
                        else if (currentPage + 1 === totalPages) {
                            links[3] = currentPage;
                        } else if (currentPage + 2 === totalPages) {
                            links[3] = currentPage + 1;
                        } else {
                            links[3] = '...';
                        }
                        break;
                    case 5:
                        // always update this to the total pages (last page to go to)
                        links[4] = totalPages;
                        break;
                }
            }

            return links;
        },

        /**
         * TODO: might need refactoring at some point?
         *
         * This function is coming from App\Support\PaginationHelpers that also has a test file
         * Fetch the pagination button links
         *
         * @return array
         * @param currentPage
         * @param totalPages
         */
        fetchLinksWithClasses(currentPage = 1, totalPages = 1)
        {
            let links = this.fetchLinks(currentPage, totalPages);

            let linksWithClasses = [];
            for (let i = 0; i < links.length; i++) {
                linksWithClasses.push({'class': '', 'label': links[i]});
            }

            // if not 5 items or if middle item is disabled - return as it is
            if (linksWithClasses.length < 5 || links[2] === '...' || (currentPage === totalPages)) {
                return linksWithClasses;
            }

            // we want to add 2 links before current and 2 after current ONLY for desktop
            let classes = 'hideMobile';

            // if links count === 5
            // if the link in middle is not ...
            // if link 2 or link 4 is ...
            // if current is in the middle
            // if current - 2 > first - allow prepend
            // if current + 2 < last - allow prepend

            let allowPrepend = ((currentPage - 2) > links[0]) && links[1] === '...';
            let allowAppend = ((currentPage + 2) < links[4]) && (links[3] === '...') && (currentPage >= 3);
            //        dump("Current: {currentPage} || Allow Prepend: {allowPrepend} ||  " . "Allow Append: {allowAppend}");

            if (allowPrepend) {
                let replacePrepend = true;
                let prependOne = currentPage - 2;
                let prependTwo = currentPage - 1;

                // do we need to replace the second link (...) or leave it
                // this means there are more `links` so we only add these two links after the ...
                if (currentPage - 3 > links[0]) {
                    replacePrepend = false;
                }

                if (replacePrepend && links[1] === '...') {
                    linksWithClasses[1]['class'] = 'showMobileOnly';
                }

                let prependItem1 = {'class': classes, 'label': prependOne};
                let prependItem2 = {'class': classes, 'label': prependTwo};

                // if second last page is selected - we only want to prepend 1 item
                if (currentPage + 1 === totalPages) {
                    linksWithClasses.splice(2, 0, prependItem1);
                } else {
                    linksWithClasses.splice(2, 0, prependItem1, prependItem2);
                }
            }

            if (allowAppend) {
                let replaceAppend = true;
                let appendOne = currentPage + 1;
                let appendTwo = currentPage + 2;

                // do we need to replace the 4th link (...) or leave it
                // this means there are more `links` so we only add these two links after the current page
                if (currentPage + 3 < links[4]) {
                    replaceAppend = false;
                }

                let replaceIndex = 3;
                if (linksWithClasses.length === 6) {
                    replaceIndex = 4;
                } else if (linksWithClasses.length === 7) {
                    replaceIndex = 5;
                } else if (linksWithClasses.length === 8) {
                    replaceIndex = 6;
                }

                if (replaceAppend && links[3] === '...') {
                    let lastDisabledSeparatorIndex = linksWithClasses.length - 2;
                    linksWithClasses[lastDisabledSeparatorIndex]['class'] = 'showMobileOnly';
                }

                let appendItem1 = {'class': classes, 'label': appendOne};
                let appendItem2 = {'class': classes, 'label': appendTwo};
                linksWithClasses.splice(replaceIndex, 0, appendItem1, appendItem2);
            }

            return linksWithClasses;
        },
    },
};
</script>
